// frontend/modules/customer-notification/hooks/useNotificationHistory.ts
import { useCallback, useEffect } from 'react';
import { useNotificationStore } from '../stores/notification-store';
import { showSuccessNotification, showErrorNotification } from '@/components/common/utils/notify';
import { useTheme } from '@/components/common/ThemeProvider';
import { CustomerNotificationHistory, NotificationCategoryType, NotificationPlatforms } from '@/shared/api/model';

// Import the API functions generated by Orval
import {
    useGetNotificationHistory,
    useGetCustomerNotificationHistory
} from '@/shared/api/client/customer-notification-controller';

export const useNotificationHistory = () => {
    const { dark } = useTheme();

    // Get state and actions from store
    const {
        notifications,
        filteredNotifications,
        isLoading,
        error,
        searchTerm,
        selectedBranch,
        selectedPlatform,
        selectedCategory,
        startDate,
        endDate,
        successStatus,

        setNotifications,
        setIsLoading,
        setError,
        setSearchTerm,
        setSelectedBranch,
        setSelectedPlatform,
        setSelectedCategory,
        setDateRange,
        setSuccessStatus,
        resetFilters,
        filterNotifications
    } = useNotificationStore();

    // API query for notification history with filters
    const {
        data: historyData,
        isLoading: isHistoryLoading,
        refetch: refetchHistory,
        error: historyError
    } = useGetNotificationHistory({
        // @ts-ignore
        params: {
            branch: selectedBranch || undefined,
            customerNumber: searchTerm.length > 0 ? searchTerm : undefined,
            category: selectedCategory || undefined,
            platform: selectedPlatform || undefined,
            success: successStatus,
            startDate: startDate ? startDate.toISOString() : undefined,
            endDate: endDate ? endDate.toISOString() : undefined,
            templateName: undefined // We handle template name filtering in our local filtering
        }
    });

    // Update notifications when data is loaded
    useEffect(() => {
        if (historyData) {
            setNotifications(historyData.content as CustomerNotificationHistory[]);
            setError(null);
        }
    }, [historyData, setNotifications, setError]);

    // Handle error state
    useEffect(() => {
        if (historyError) {
            console.error('Error loading notification history:', historyError);
            setError('Failed to load notification history');
            showErrorNotification('Error', 'Failed to load notification history', dark);
        }
    }, [historyError, setError, dark]);

    // Set overall loading state
    useEffect(() => {
        setIsLoading(isHistoryLoading);
    }, [isHistoryLoading, setIsLoading]);

    // Load notifications for a specific customer
    const loadCustomerNotifications = useCallback((branch: string, customerNumber: string) => {
        // Fixed: Correctly use the query hook instead of treating it as a mutation
        const {
            refetch: fetchCustomerHistory,
            isLoading: isCustomerHistoryLoading
        } = useGetCustomerNotificationHistory(
            branch,
            customerNumber
        );

        setIsLoading(true);

        fetchCustomerHistory()
            .then((response: any) => {
                if (response) {
                    setNotifications(response);
                    showSuccessNotification(
                        'Success',
                        `Loaded ${(response as any)?.length || 0} notifications for customer ${customerNumber}`,
                        dark
                    );
                }
            })
            .catch(error => {
                console.error('Error loading customer notifications:', error);
                setError('Failed to load customer notifications');
                showErrorNotification('Error', 'Failed to load customer notifications', dark);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [setIsLoading, setNotifications, setError, dark]);

    // Reload notification history
    const refreshNotifications = useCallback(() => {
        refetchHistory();
    }, [refetchHistory]);

    return {
        // State
        notifications,
        filteredNotifications,
        isLoading,
        error,
        searchTerm,
        selectedBranch,
        selectedPlatform,
        selectedCategory,
        startDate,
        endDate,
        successStatus,

        // Actions
        setSearchTerm,
        setSelectedBranch,
        setSelectedPlatform,
        setSelectedCategory,
        setDateRange,
        setSuccessStatus,
        resetFilters,
        refreshNotifications,
        loadCustomerNotifications
    };
};